/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import {
  NegotiableQuoteTemplateModel,
  NegotiableQuoteTemplatesListModel,
  NegotiableQuoteTemplateStatus,
} from '../models/negotiable-quote-template-model';
import { transformPaginationInfo } from './transform-quote';
import { transformHistory } from './transform-history';
import { transformQuoteItems } from './transform-quote-items';

export function transformQuoteTemplate(
  templateData: any
): NegotiableQuoteTemplateModel | null {
  if (!templateData) {
    return null;
  }

  return {
    id: templateData.template_id,
    uid: templateData.uid,
    name: templateData.name,
    createdAt: templateData.created_at,
    updatedAt: templateData.updated_at,
    expirationDate: templateData.expiration_date,
    status: templateData.status,
    salesRepName: templateData.sales_rep_name,
    buyer: {
      firstname: templateData.buyer.firstname,
      lastname: templateData.buyer.lastname,
    },
    comments: templateData.comments?.map((comment: any) => ({
      uid: comment.uid,
      createdAt: comment.created_at,
      author: {
        firstname: comment.author.firstname,
        lastname: comment.author.lastname,
      },
      text: comment.text,
      attachments: comment.attachments?.map((a: any) => ({
        name: a.name,
        url: a.url,
      })),
    })),
    prices: templateData.prices && {
      subtotalExcludingTax: templateData.prices.subtotal_excluding_tax && {
        value: templateData.prices.subtotal_excluding_tax.value,
        currency: templateData.prices.subtotal_excluding_tax.currency,
      },
      subtotalIncludingTax: templateData.prices.subtotal_including_tax && {
        value: templateData.prices.subtotal_including_tax.value,
        currency: templateData.prices.subtotal_including_tax.currency,
      },
      subtotalWithDiscountExcludingTax: templateData.prices
        .subtotal_with_discount_excluding_tax && {
        value: templateData.prices.subtotal_with_discount_excluding_tax.value,
        currency:
          templateData.prices.subtotal_with_discount_excluding_tax.currency,
      },
      appliedTaxes: templateData.prices.applied_taxes?.map((tax: any) => ({
        amount: { value: tax.amount.value, currency: tax.amount.currency },
        label: tax.label,
      })),
      grandTotal: templateData.prices.grand_total && {
        value: templateData.prices.grand_total.value,
        currency: templateData.prices.grand_total.currency,
      },
    },
    history: transformHistory(templateData.history),
    items: transformQuoteItems(templateData.items),
    shippingAddresses: templateData.shipping_addresses?.map((address: any) => ({
      uid: address.uid,
      customerAddressUid: address.customer_address_uid,
      companyAddressId: address.company_address_id,
      firstname: address.firstname,
      lastname: address.lastname,
      company: address.company,
      street: address.street,
      city: address.city,
      postcode: address.postcode,
      country: {
        code: address.country.code,
        label: address.country.label,
      },
      telephone: address.telephone,
      region: address.region
        ? {
            code: address.region.code,
            label: address.region.label,
            regionId: address.region.region_id,
          }
        : undefined,
    })),
    referenceDocuments: templateData.reference_document_links?.map((doc: any) => ({
      uid: doc.link_id,
      name: doc.document_name,
      identifier: doc.document_identifier,
      url: doc.reference_document_url,
    })),
    // Calculate permissions based on status
    canAccept: [
      NegotiableQuoteTemplateStatus.SUBMITTED,
      NegotiableQuoteTemplateStatus.PENDING,
      NegotiableQuoteTemplateStatus.OPEN,
    ].includes(templateData.status),
    canDelete: templateData.status === NegotiableQuoteTemplateStatus.DRAFT,
    canReopen: [
      NegotiableQuoteTemplateStatus.DECLINED,
      NegotiableQuoteTemplateStatus.CANCELED,
      NegotiableQuoteTemplateStatus.EXPIRED,
    ].includes(templateData.status),
    canCancel: [
      NegotiableQuoteTemplateStatus.SUBMITTED,
      NegotiableQuoteTemplateStatus.PENDING,
      NegotiableQuoteTemplateStatus.OPEN,
      NegotiableQuoteTemplateStatus.ACTIVE,
      NegotiableQuoteTemplateStatus.EDITED,
    ].includes(templateData.status),
    canSendForReview: [
      NegotiableQuoteTemplateStatus.OPEN,
      NegotiableQuoteTemplateStatus.DRAFT,
      NegotiableQuoteTemplateStatus.NEW,
      NegotiableQuoteTemplateStatus.EDITED,
    ].includes(templateData.status),
    canGenerateQuoteFromTemplate: [
      NegotiableQuoteTemplateStatus.ACTIVE,
    ].includes(templateData.status),
    canEditTemplateItems: [
      NegotiableQuoteTemplateStatus.SUBMITTED,
      NegotiableQuoteTemplateStatus.DRAFT,
      NegotiableQuoteTemplateStatus.OPEN,
      NegotiableQuoteTemplateStatus.EDITED,
    ].includes(templateData.status),
  };
}

export function transformQuoteTemplatesList(
  templatesData: any
): NegotiableQuoteTemplatesListModel | null {
  if (!templatesData) {
    return null;
  }

  const transformedModel = {
    items:
      templatesData.items?.map((template: any) => ({
        id: template.template_id,
        uid: template.uid,
        name: template.name,
        createdAt: template.created_at,
        updatedAt: template.updated_at,
        status: template.status,
        state: template.state,
        prices: {
          grandTotal: template.prices?.grand_total
            ? {
                value: template.prices.grand_total.value,
                currency: template.prices.grand_total.currency,
              }
            : {
                value: 0,
                currency: 'USD',
              },
          minNegotiatedGrandTotal: {
            value: template.min_negotiated_grand_total,
            currency: template.prices?.grand_total?.currency || 'USD',
          },
        },
        lastSharedAt: template.last_shared_at,
        lastOrderedAt: template.last_ordered_at,
        expirationDate: template.expiration_date,
        ordersPlaced: template.orders_placed,
        canGenerateQuoteFromTemplate: [
          NegotiableQuoteTemplateStatus.ACTIVE,
        ].includes(template.status),
      })) || [],
    pageInfo: {
      currentPage: templatesData.page_info.current_page,
      pageSize: templatesData.page_info.page_size,
      totalPages: templatesData.page_info.total_pages,
    },
    totalCount: templatesData.total_count,
    sortFields: templatesData.sort_fields
      ? {
          default: templatesData.sort_fields.default,
          options: templatesData.sort_fields.options,
        }
      : undefined,
  };

  // Calculate pagination info
  const paginationInfo = transformPaginationInfo(transformedModel);

  return {
    ...transformedModel,
    paginationInfo: paginationInfo || undefined,
  };
}
